import React from "react";
import { LegalLayout, Section } from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <LegalLayout title="Privacy Policy" updated="2026-05-17">
        <p className="text-zinc-300 leading-relaxed text-[15px]">
          This Privacy Policy explains how CloudRent Hosting Ltd. ("we", "us", "our")
          collects, uses, and shares personal data when you use the CloudRent cloud
          hosting platform (the "Service"). We comply with the EU GDPR, the UK GDPR,
          and the California Consumer Privacy Act (CCPA).
        </p>

        <Section n={1} title="Information We Collect">
          <p>We collect the following categories of personal data:</p>
          <ul className="list-disc list-inside space-y-1 text-zinc-300">
            <li><strong>Account data</strong>: name, email, hashed password, role.</li>
            <li><strong>Billing data</strong>: billing country, VAT ID (if provided). Card numbers are entered on our checkout provider's hosted environment; only an opaque reference and the card's last 4 digits reach our servers.</li>
            <li><strong>Order data</strong>: server plans purchased, amount, status, server credentials issued.</li>
            <li><strong>Usage data</strong>: IP address (for security), browser user-agent, log timestamps.</li>
            <li><strong>Communications</strong>: any messages you send to support.</li>
          </ul>
          <p>
            We <strong>never</strong> receive or store your raw card number, CVC, or
            expiry date. These are collected by our designated checkout provider on
            their certified environment. Only an opaque reference and the card's last
            four digits are stored on our servers for receipt-display purposes.
          </p>
        </Section>

        <Section n={2} title="Why We Process Your Data">
          <ul className="list-disc list-inside space-y-1 text-zinc-300">
            <li>To create and operate your account (contract).</li>
            <li>To deliver paid servers and credentials (contract).</li>
            <li>To process payments and prevent fraud (legitimate interest & legal obligation).</li>
            <li>To provide customer support (contract).</li>
            <li>To comply with tax and accounting obligations (legal obligation).</li>
            <li>To send transactional emails about your orders (contract).</li>
          </ul>
        </Section>

        <Section n={3} title="Who We Share Data With">
          <p>We share data only with vetted sub-processors strictly necessary to run the Service:</p>
          <ul className="list-disc list-inside space-y-1 text-zinc-300">
            <li><strong>Designated checkout provider</strong> — collects card data on their certified environment, processes payments, and remits funds to us. Identity disclosed at checkout.</li>
            <li><strong>MongoDB Atlas</strong> — primary database hosting (EU region).</li>
            <li><strong>Cloud infrastructure provider</strong> — compute & networking (EU/US regions).</li>
            <li><strong>Email delivery provider</strong> — transactional email only.</li>
          </ul>
          <p>
            We do not sell or rent your personal data. We do not share it for
            cross-context behavioural advertising.
          </p>
        </Section>

        <Section n={4} title="International Transfers">
          <p>
            Where data is transferred outside the EEA / UK, we rely on the European
            Commission's Standard Contractual Clauses (SCCs) or an adequacy decision.
          </p>
        </Section>

        <Section n={5} title="Retention">
          <p>
            We keep account data while your account is active and up to 7 years after
            closure for tax/audit reasons. Payment logs are kept for 7 years to satisfy
            financial-record obligations. You can request earlier deletion of
            non-mandatory data at any time.
          </p>
        </Section>

        <Section n={6} title="Your Rights">
          <p>You have the right to:</p>
          <ul className="list-disc list-inside space-y-1 text-zinc-300">
            <li>Access a copy of the personal data we hold about you.</li>
            <li>Correct inaccurate data.</li>
            <li>Erase your data (subject to legal-retention obligations).</li>
            <li>Restrict or object to certain processing.</li>
            <li>Port your data to another provider (machine-readable format).</li>
            <li>Lodge a complaint with your local data-protection authority.</li>
          </ul>
          <p>
            Submit any request to <a className="text-signal hover:underline" href="mailto:privacy@cloudrentvps.com">privacy@cloudrentvps.com</a>.
            We respond within 30 days.
          </p>
        </Section>

        <Section n={7} title="Cookies">
          <p>
            We use only first-party, strictly-necessary cookies: a session cookie for
            authentication (httpOnly, SameSite=Lax) and a CSRF token. We do not use
            third-party advertising cookies that require consent under EU law.
          </p>
        </Section>

        <Section n={8} title="Security">
          <p>
            Passwords are hashed with bcrypt. Payment data is never stored on our
            servers — all card information is handled by our designated checkout
            provider on their certified infrastructure. Database backups are encrypted
            at rest and in transit. Internal billing events are signature-verified and
            written to an immutable audit log before they trigger any provisioning.
          </p>
        </Section>

        <Section n={9} title="Contact">
          <p>
            Data Protection Officer: <a className="text-signal hover:underline" href="mailto:dpo@cloudrentvps.com">dpo@cloudrentvps.com</a>
          </p>
        </Section>
      </LegalLayout>
    </div>
  );
}
